import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStoryJobStream } from '@/hooks/useStoryJobStream';
import { StoryErrorBlock } from '@/components/patterns/StoryErrorBlock';
import {
  limitBlockErrorVariant,
  technicalErrorVariant,
  type VariantHandlers,
} from '@/features/story/error-variants';
import { getErrorVariant } from '@/lib/errors';
import { type WorldSlug } from '@/lib/story-output/progress-timer';

// State passed by the builder's navigate() call. wantsAudio/wantsPdf affect
// which loading label renders at the audio/pdf pipeline steps.
interface OutputNavState {
  world?: WorldSlug;
  wantsAudio?: boolean;
  wantsPdf?: boolean;
}

// Per-world solid bg matching the Phase 4 Story-Output routes (PWA #16
// Sub-Phase 16.4 — replaces the prior Builder-style gradients). Static
// literal map so Tailwind JIT picks up all 3 classes. Loading-Screen
// keeps the visual focus on the centered video — no corner / above /
// full-bleed illustrations on this page (Sebastian Gate-1 2026-04-29).
const WORLD_BG: Record<WorldSlug, string> = {
  aa: 'bg-world-aa',
  kp: 'bg-world-kp',
  sb: 'bg-world-sb',
};
// Mirrors the Phase 4 negative-margin trick on the Story-Output routes
// — extends the route's bg over AppShell's <main> pb gap so cream
// doesn't peek through below content on either the loading or failed
// state.
const NEG_MARGIN_BG_EXTEND =
  '-mb-[calc(10rem+env(safe-area-inset-bottom,0))] pb-[calc(10rem+env(safe-area-inset-bottom,0))]';

export function OutputRoute() {
  const { jobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const navState = (location.state ?? {}) as OutputNavState;
  const world: WorldSlug = navState.world ?? 'aa';
  // wantsAudio / wantsPdf remain on nav-state for future wiring on the story
  // page (audio + PDF polling) but are no longer consumed here — the timer
  // labels no longer distinguish audio/pdf phases.

  const { displayPercent, currentLabel, status, storyId, errorCode, userMessage } =
    useStoryJobStream(jobId ?? '', { world });

  // Lazy-mount the loading video — gives the SSE connection a 500ms head start
  // so the video's 3–5MB download doesn't contend with stream-establishment
  // bandwidth on iOS Safari. The empty video slot during the delay is
  // near-invisible since the progress UI is the primary feedback.
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowVideo(true), 500);
    return () => clearTimeout(t);
  }, []);

  // Early-navigation: jump to the story page as soon as storyId is assigned
  // by the backend's generate_story step, regardless of whether audio/PDF are
  // still processing. The story page shows placeholder states for in-transit
  // assets. Failure keeps the user on the loading screen so the error UI
  // below can render. Intentionally not in-hook — keeps the hook pure;
  // parent owns routing.
  useEffect(() => {
    if (storyId && status !== 'failed') {
      navigate(`/story/${storyId}`, { replace: true });
    }
  }, [status, storyId, navigate]);

  // KP uses navy/dark band at the top — label + percent need white text there.
  // AA + SB gradients stay cream-ish near the form zone — Navy text reads fine.
  const textColor = world === 'kp' ? 'text-dream-white' : 'text-dream-navy';

  // Batch 12 Sub-phase B — replaced the placeholder failure UI with
  // variant-driven StoryErrorBlock. errorCode → variant routing via
  // getErrorVariant: `blocked_limit` → Limit-Block (Käpt'n Kuschel +
  // Abo/Pack CTAs — Option B defensive surface for mid-flow drain),
  // everything else → technical (Flitzi + Retry).
  if (status === 'failed') {
    const variant = getErrorVariant(errorCode);
    const handlers: VariantHandlers = {
      onRetry: () => navigate(`/builder/${world}`, { replace: true }),
      onGoToLibrary: () => navigate('/library'),
      onChoosePlan: () => navigate('/account/subscription'),
      onBuyPack: () => navigate('/account/subscription'),
    };
    const props =
      variant === 'limitBlock' ? limitBlockErrorVariant(handlers) : technicalErrorVariant(handlers);
    // Workstream F.6 — pass backend user_message via the hybrid envelope.
    // Conditional spread to satisfy exactOptionalPropertyTypes: when
    // userMessage is null, errorEnvelope is omitted entirely (not passed
    // as undefined). The .trim()-based fallback inside StoryErrorBlock
    // still handles empty user_message strings.
    return (
      <div
        className={`relative min-h-screen overflow-hidden ${NEG_MARGIN_BG_EXTEND} ${WORLD_BG[world]}`}
      >
        <StoryErrorBlock
          {...props}
          {...(userMessage ? { errorEnvelope: { user_message: userMessage } } : {})}
          testId="output-error-block"
        />
      </div>
    );
  }

  const label = currentLabel;
  const percent = Math.floor(displayPercent);
  // 300ms smooth transition on the final 95→100 bump gives a visible polish
  // beat. Mid-stream ticks run at 100ms so they feel live, not laggy.
  const progressDurationClass = status === 'completed' ? 'duration-300' : 'duration-100';

  return (
    <div
      className={`relative min-h-screen overflow-hidden ${NEG_MARGIN_BG_EXTEND} ${WORLD_BG[world]}`}
    >
      <div className="mx-auto flex max-w-[560px] desktop:max-w-[720px] flex-col items-center px-6 pt-16 pb-12">
        <div className="mb-8 flex aspect-video w-[90vw] max-w-[420px] items-center justify-center md:w-[480px] md:max-w-none desktop:w-[720px]">
          {showVideo && (
            <video
              src={`/videos/loading-${world}.mp4`}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden
              className="h-full w-full object-contain"
            />
          )}
        </div>

        <p className={`mb-4 text-center text-base ${textColor}`}>{label}</p>

        <div
          className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-dream-navy/15"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full bg-dream-gold transition-[width] ease-linear ${progressDurationClass}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        <p className={`mt-2 text-sm font-medium ${textColor}`}>{percent}%</p>
      </div>
    </div>
  );
}
